/**
 * London neighbourhood coordinates and metadata for local SEO
 * Each area includes precise lat/lng, postcode district, and nearby areas for internal linking
 */

export interface AreaData {
  name: string;
  slug: string;
  lat: string;
  lng: string;
  postcode: string;
  borough: string;
  region: string;
  nearbyAreas: string[]; // slugs
  landmarks?: string[];
  propertyTypes?: string[];
}

export const londonAreaCoordinates: Record<string, AreaData> = {
  "mayfair": {
    name: "Mayfair", slug: "mayfair-secondary-glazing",
    lat: "51.5099", lng: "-0.1468", postcode: "W1", borough: "City of Westminster", region: "Central London",
    nearbyAreas: ["soho", "st-james", "knightsbridge", "marylebone", "fitzrovia"],
    landmarks: ["Berkeley Square", "Grosvenor Square", "The Ritz", "Bond Street"],
    propertyTypes: ["Georgian townhouses", "mansion blocks", "luxury apartments"]
  },
  "kensington": {
    name: "Kensington", slug: "kensington-secondary-glazing",
    lat: "51.4990", lng: "-0.1939", postcode: "W8", borough: "Royal Borough of Kensington and Chelsea", region: "West London",
    nearbyAreas: ["holland-park", "notting-hill", "chelsea", "knightsbridge", "earls-court", "south-kensington"],
    landmarks: ["Kensington Palace", "Kensington High Street", "Holland Park"],
    propertyTypes: ["Victorian terraces", "stucco-fronted townhouses", "mansion blocks"]
  },
  "chelsea": {
    name: "Chelsea", slug: "chelsea-secondary-glazing",
    lat: "51.4875", lng: "-0.1687", postcode: "SW3", borough: "Royal Borough of Kensington and Chelsea", region: "West London",
    nearbyAreas: ["kensington", "knightsbridge", "fulham", "pimlico", "battersea"],
    landmarks: ["King's Road", "Sloane Square", "Chelsea Embankment", "Royal Hospital Chelsea"],
    propertyTypes: ["Georgian terraces", "Victorian townhouses", "garden squares"]
  },
  "knightsbridge": {
    name: "Knightsbridge", slug: "knightsbridge-secondary-glazing",
    lat: "51.5013", lng: "-0.1596", postcode: "SW1X", borough: "City of Westminster", region: "Central London",
    nearbyAreas: ["chelsea", "kensington", "mayfair", "belgravia", "south-kensington"],
    landmarks: ["Harrods", "Harvey Nichols", "Hyde Park", "Brompton Oratory"],
    propertyTypes: ["Period mansion blocks", "luxury apartments", "Victorian townhouses"]
  },
  "hampstead": {
    name: "Hampstead", slug: "hampstead-secondary-glazing",
    lat: "51.5563", lng: "-0.1780", postcode: "NW3", borough: "Camden", region: "North London",
    nearbyAreas: ["highgate", "primrose-hill", "st-johns-wood", "swiss-cottage", "belsize-park"],
    landmarks: ["Hampstead Heath", "Kenwood House", "Hampstead Village", "Parliament Hill"],
    propertyTypes: ["Georgian villas", "Victorian detached houses", "Arts & Crafts homes"]
  },
  "notting-hill": {
    name: "Notting Hill", slug: "notting-hill-secondary-glazing",
    lat: "51.5093", lng: "-0.1965", postcode: "W11", borough: "Royal Borough of Kensington and Chelsea", region: "West London",
    nearbyAreas: ["holland-park", "kensington", "bayswater", "shepherds-bush", "maida-vale"],
    landmarks: ["Portobello Road Market", "Ladbroke Grove", "Westbourne Grove"],
    propertyTypes: ["Victorian stucco terraces", "converted flats", "garden square properties"]
  },
  "fulham": {
    name: "Fulham", slug: "fulham-secondary-glazing",
    lat: "51.4749", lng: "-0.2028", postcode: "SW6", borough: "Hammersmith and Fulham", region: "West London",
    nearbyAreas: ["chelsea", "hammersmith", "putney", "earls-court", "west-brompton"],
    landmarks: ["Fulham Palace", "Bishop's Park", "Hurlingham Club"],
    propertyTypes: ["Victorian terraces", "Edwardian houses", "mansion flats"]
  },
  "bloomsbury": {
    name: "Bloomsbury", slug: "bloomsbury-secondary-glazing",
    lat: "51.5236", lng: "-0.1276", postcode: "WC1", borough: "Camden", region: "Central London",
    nearbyAreas: ["fitzrovia", "holborn", "kings-cross", "euston", "covent-garden"],
    landmarks: ["British Museum", "Russell Square", "University of London", "Bloomsbury Square"],
    propertyTypes: ["Georgian terraces", "Grade II listed townhouses", "mansion blocks"]
  },
  "marylebone": {
    name: "Marylebone", slug: "marylebone-secondary-glazing",
    lat: "51.5225", lng: "-0.1534", postcode: "W1U", borough: "City of Westminster", region: "Central London",
    nearbyAreas: ["mayfair", "fitzrovia", "paddington", "regents-park", "bayswater"],
    landmarks: ["Marylebone High Street", "Regent's Park", "Baker Street", "Wallace Collection"],
    propertyTypes: ["Georgian townhouses", "Victorian mansion blocks", "Edwardian apartments"]
  },
  "paddington": {
    name: "Paddington", slug: "paddington-secondary-glazing",
    lat: "51.5154", lng: "-0.1755", postcode: "W2", borough: "City of Westminster", region: "Central London",
    nearbyAreas: ["bayswater", "marylebone", "maida-vale", "notting-hill"],
    landmarks: ["Paddington Station", "Little Venice", "Norfolk Square"],
    propertyTypes: ["Victorian terraces", "stucco-fronted houses", "mansion blocks"]
  },
  "richmond": {
    name: "Richmond", slug: "richmond-secondary-glazing",
    lat: "51.4613", lng: "-0.3037", postcode: "TW9", borough: "Richmond upon Thames", region: "South West London",
    nearbyAreas: ["kew", "twickenham", "barnes", "hampton"],
    landmarks: ["Richmond Park", "Richmond Bridge", "Richmond Green", "Richmond Theatre"],
    propertyTypes: ["Georgian townhouses", "Victorian villas", "riverside properties"]
  },
  "greenwich": {
    name: "Greenwich", slug: "greenwich-secondary-glazing",
    lat: "51.4769", lng: "-0.0005", postcode: "SE10", borough: "Royal Borough of Greenwich", region: "South East London",
    nearbyAreas: ["bermondsey", "southwark", "london-bridge"],
    landmarks: ["Greenwich Observatory", "Cutty Sark", "Maritime Museum", "Greenwich Park"],
    propertyTypes: ["Georgian terraces", "Victorian houses", "converted warehouses"]
  },
  "wimbledon": {
    name: "Wimbledon", slug: "wimbledon-secondary-glazing",
    lat: "51.4214", lng: "-0.2064", postcode: "SW19", borough: "Merton", region: "South West London",
    nearbyAreas: ["putney", "wandsworth", "merton", "balham", "tooting"],
    landmarks: ["Wimbledon Common", "All England Club", "Wimbledon Village"],
    propertyTypes: ["Victorian villas", "Edwardian houses", "detached family homes"]
  },
  "clapham": {
    name: "Clapham", slug: "clapham-secondary-glazing",
    lat: "51.4625", lng: "-0.1384", postcode: "SW4", borough: "Lambeth", region: "South London",
    nearbyAreas: ["battersea", "brixton", "balham", "stockwell", "wandsworth"],
    landmarks: ["Clapham Common", "Clapham Old Town", "Clapham Junction"],
    propertyTypes: ["Victorian terraces", "Georgian houses", "converted flats"]
  },
  "battersea": {
    name: "Battersea", slug: "battersea-secondary-glazing",
    lat: "51.4762", lng: "-0.1486", postcode: "SW11", borough: "Wandsworth", region: "South London",
    nearbyAreas: ["chelsea", "clapham", "wandsworth", "pimlico", "vauxhall"],
    landmarks: ["Battersea Park", "Battersea Power Station", "Albert Bridge"],
    propertyTypes: ["Victorian terraces", "mansion blocks", "new-build apartments"]
  },
  "soho": {
    name: "Soho", slug: "soho-secondary-glazing",
    lat: "51.5133", lng: "-0.1329", postcode: "W1D", borough: "City of Westminster", region: "Central London",
    nearbyAreas: ["mayfair", "covent-garden", "fitzrovia", "st-james"],
    landmarks: ["Carnaby Street", "Soho Square", "Dean Street", "Old Compton Street"],
    propertyTypes: ["Georgian townhouses", "above-shop flats", "loft conversions"]
  },
  "fitzrovia": {
    name: "Fitzrovia", slug: "fitzrovia-secondary-glazing",
    lat: "51.5197", lng: "-0.1384", postcode: "W1T", borough: "Camden", region: "Central London",
    nearbyAreas: ["bloomsbury", "marylebone", "soho", "euston"],
    landmarks: ["BT Tower", "Charlotte Street", "Fitzroy Square"],
    propertyTypes: ["Georgian terraces", "Victorian mansion blocks", "mixed-use buildings"]
  },
  "pimlico": {
    name: "Pimlico", slug: "pimlico-secondary-glazing",
    lat: "51.4893", lng: "-0.1346", postcode: "SW1V", borough: "City of Westminster", region: "Central London",
    nearbyAreas: ["westminster", "chelsea", "belgravia", "vauxhall", "battersea"],
    landmarks: ["Tate Britain", "Pimlico Gardens", "Churchill Gardens Estate"],
    propertyTypes: ["Regency terraces", "stucco-fronted houses", "mansion blocks"]
  },
  "westminster": {
    name: "Westminster", slug: "westminster-secondary-glazing",
    lat: "51.4975", lng: "-0.1357", postcode: "SW1", borough: "City of Westminster", region: "Central London",
    nearbyAreas: ["pimlico", "mayfair", "st-james", "waterloo", "vauxhall"],
    landmarks: ["Houses of Parliament", "Westminster Abbey", "Buckingham Palace"],
    propertyTypes: ["Georgian townhouses", "listed buildings", "mansion blocks"]
  },
  "belgravia": {
    name: "Belgravia", slug: "belgravia-secondary-glazing",
    lat: "51.4976", lng: "-0.1534", postcode: "SW1X", borough: "City of Westminster", region: "Central London",
    nearbyAreas: ["knightsbridge", "chelsea", "pimlico", "westminster", "mayfair"],
    landmarks: ["Belgrave Square", "Eaton Square", "Chester Square"],
    propertyTypes: ["Regency stucco terraces", "Grade I & II listed townhouses", "garden square properties"]
  },
  "holland-park": {
    name: "Holland Park", slug: "holland-park-secondary-glazing",
    lat: "51.5027", lng: "-0.2055", postcode: "W11", borough: "Royal Borough of Kensington and Chelsea", region: "West London",
    nearbyAreas: ["kensington", "notting-hill", "shepherds-bush", "hammersmith"],
    landmarks: ["Holland Park", "Holland House", "Design Museum"],
    propertyTypes: ["Victorian villas", "large detached houses", "converted mansion flats"]
  },
  "chiswick": {
    name: "Chiswick", slug: "chiswick-secondary-glazing",
    lat: "51.4925", lng: "-0.2591", postcode: "W4", borough: "Hounslow", region: "West London",
    nearbyAreas: ["hammersmith", "acton", "barnes", "brentford", "ealing"],
    landmarks: ["Chiswick House", "Hogarth's House", "Strand on the Green"],
    propertyTypes: ["Edwardian terraces", "Victorian houses", "riverside properties"]
  },
  "ealing": {
    name: "Ealing", slug: "ealing-secondary-glazing",
    lat: "51.5130", lng: "-0.3089", postcode: "W5", borough: "Ealing", region: "West London",
    nearbyAreas: ["acton", "chiswick", "greenford", "north-acton", "brentford"],
    landmarks: ["Ealing Broadway", "Pitzhanger Manor", "Walpole Park"],
    propertyTypes: ["Edwardian houses", "1930s semi-detached", "Victorian terraces"]
  },
  "highgate": {
    name: "Highgate", slug: "highgate-secondary-glazing",
    lat: "51.5714", lng: "-0.1463", postcode: "N6", borough: "Haringey/Camden", region: "North London",
    nearbyAreas: ["hampstead", "primrose-hill"],
    landmarks: ["Highgate Cemetery", "Waterlow Park", "Highgate Village"],
    propertyTypes: ["Georgian villas", "Victorian detached houses", "Arts & Crafts homes"]
  },
  "st-johns-wood": {
    name: "St John's Wood", slug: "st-johns-wood-secondary-glazing",
    lat: "51.5340", lng: "-0.1737", postcode: "NW8", borough: "City of Westminster", region: "North London",
    nearbyAreas: ["maida-vale", "primrose-hill", "regents-park", "swiss-cottage", "marylebone"],
    landmarks: ["Lord's Cricket Ground", "Abbey Road Studios", "Regent's Park"],
    propertyTypes: ["Victorian villas", "detached houses", "mansion blocks"]
  },
  "primrose-hill": {
    name: "Primrose Hill", slug: "primrose-hill-secondary-glazing",
    lat: "51.5392", lng: "-0.1601", postcode: "NW1", borough: "Camden", region: "North London",
    nearbyAreas: ["hampstead", "st-johns-wood", "regents-park", "swiss-cottage"],
    landmarks: ["Primrose Hill viewpoint", "Regent's Park", "Chalk Farm"],
    propertyTypes: ["Regency terraces", "Victorian cottages", "pastel-coloured townhouses"]
  },
  "shoreditch": {
    name: "Shoreditch", slug: "shoreditch-secondary-glazing",
    lat: "51.5264", lng: "-0.0780", postcode: "EC2A", borough: "Hackney", region: "East London",
    nearbyAreas: ["brick-lane", "spitalfields", "clerkenwell", "aldgate", "whitechapel"],
    landmarks: ["Old Street roundabout", "Boxpark", "Columbia Road"],
    propertyTypes: ["Converted warehouses", "Victorian terraces", "loft apartments"]
  },
  "spitalfields": {
    name: "Spitalfields", slug: "spitalfields-secondary-glazing",
    lat: "51.5185", lng: "-0.0764", postcode: "E1", borough: "Tower Hamlets", region: "East London",
    nearbyAreas: ["brick-lane", "shoreditch", "aldgate", "whitechapel", "city-of-london"],
    landmarks: ["Spitalfields Market", "Christ Church", "Brick Lane"],
    propertyTypes: ["Georgian weavers' houses", "Victorian terraces", "converted warehouses"]
  },
  "covent-garden": {
    name: "Covent Garden", slug: "covent-garden-secondary-glazing",
    lat: "51.5117", lng: "-0.1240", postcode: "WC2E", borough: "City of Westminster", region: "Central London",
    nearbyAreas: ["soho", "bloomsbury", "strand", "holborn"],
    landmarks: ["Covent Garden Market", "Royal Opera House", "Seven Dials"],
    propertyTypes: ["Georgian townhouses", "converted warehouses", "above-shop apartments"]
  },
  "south-kensington": {
    name: "South Kensington", slug: "south-kensington-secondary-glazing",
    lat: "51.4941", lng: "-0.1746", postcode: "SW7", borough: "Royal Borough of Kensington and Chelsea", region: "West London",
    nearbyAreas: ["kensington", "chelsea", "knightsbridge", "earls-court", "brompton"],
    landmarks: ["Natural History Museum", "V&A Museum", "Science Museum", "Imperial College"],
    propertyTypes: ["Victorian terraces", "mansion blocks", "stucco-fronted townhouses"]
  },
  "city-of-london": {
    name: "City of London", slug: "city-of-london-secondary-glazing",
    lat: "51.5155", lng: "-0.0922", postcode: "EC", borough: "City of London", region: "Central London",
    nearbyAreas: ["clerkenwell", "aldgate", "spitalfields", "farringdon", "london-bridge"],
    landmarks: ["St Paul's Cathedral", "Bank of England", "Tower of London", "Barbican"],
    propertyTypes: ["Listed buildings", "converted offices", "Barbican flats"]
  },
  "wandsworth": {
    name: "Wandsworth", slug: "wandsworth-secondary-glazing",
    lat: "51.4571", lng: "-0.1918", postcode: "SW18", borough: "Wandsworth", region: "South West London",
    nearbyAreas: ["putney", "battersea", "clapham", "tooting", "balham"],
    landmarks: ["Wandsworth Common", "Wandsworth Town centre", "River Wandle"],
    propertyTypes: ["Victorian terraces", "Edwardian houses", "1930s semis"]
  },
  "putney": {
    name: "Putney", slug: "putney-secondary-glazing",
    lat: "51.4614", lng: "-0.2166", postcode: "SW15", borough: "Wandsworth", region: "South West London",
    nearbyAreas: ["wandsworth", "fulham", "barnes", "roehampton", "wimbledon"],
    landmarks: ["Putney Bridge", "Putney Heath", "Putney High Street"],
    propertyTypes: ["Victorian terraces", "Edwardian houses", "riverside apartments"]
  },
  "dulwich": {
    name: "Dulwich", slug: "dulwich-secondary-glazing",
    lat: "51.4453", lng: "-0.0838", postcode: "SE21", borough: "Southwark", region: "South London",
    nearbyAreas: ["brixton", "norwood", "crystal-palace", "bermondsey"],
    landmarks: ["Dulwich Picture Gallery", "Dulwich Park", "Dulwich Village"],
    propertyTypes: ["Victorian villas", "Edwardian detached houses", "period cottages"]
  },
  "brixton": {
    name: "Brixton", slug: "brixton-secondary-glazing",
    lat: "51.4613", lng: "-0.1156", postcode: "SW2", borough: "Lambeth", region: "South London",
    nearbyAreas: ["clapham", "stockwell", "dulwich", "streatham", "balham"],
    landmarks: ["Brixton Market", "Brixton Academy", "Windrush Square"],
    propertyTypes: ["Victorian terraces", "Edwardian houses", "converted flats"]
  },
  "balham": {
    name: "Balham", slug: "balham-secondary-glazing",
    lat: "51.4433", lng: "-0.1526", postcode: "SW12", borough: "Wandsworth", region: "South West London",
    nearbyAreas: ["clapham", "tooting", "streatham", "wandsworth"],
    landmarks: ["Balham High Road", "Tooting Bec Common"],
    propertyTypes: ["Victorian terraces", "Edwardian houses", "1930s mansion flats"]
  },
  "tooting": {
    name: "Tooting", slug: "tooting-secondary-glazing",
    lat: "51.4274", lng: "-0.1685", postcode: "SW17", borough: "Wandsworth", region: "South West London",
    nearbyAreas: ["balham", "streatham", "wandsworth", "wimbledon", "merton"],
    landmarks: ["Tooting Bec Lido", "Tooting Common", "Tooting Broadway"],
    propertyTypes: ["Victorian terraces", "Edwardian houses", "1930s semis"]
  },
  "streatham": {
    name: "Streatham", slug: "streatham-secondary-glazing",
    lat: "51.4279", lng: "-0.1232", postcode: "SW16", borough: "Lambeth", region: "South London",
    nearbyAreas: ["balham", "brixton", "norwood", "tooting", "dulwich"],
    landmarks: ["Streatham Common", "Streatham High Road", "Streatham Ice Rink"],
    propertyTypes: ["Victorian terraces", "Edwardian semis", "1930s houses"]
  },
  "waterloo": {
    name: "Waterloo", slug: "waterloo-secondary-glazing",
    lat: "51.5036", lng: "-0.1143", postcode: "SE1", borough: "Lambeth", region: "South London",
    nearbyAreas: ["south-bank", "southwark", "vauxhall", "westminster", "london-bridge"],
    landmarks: ["Waterloo Station", "South Bank Centre", "London Eye", "IMAX"],
    propertyTypes: ["Victorian terraces", "mansion blocks", "converted warehouses"]
  },
  "vauxhall": {
    name: "Vauxhall", slug: "vauxhall-secondary-glazing",
    lat: "51.4861", lng: "-0.1228", postcode: "SW8", borough: "Lambeth", region: "South London",
    nearbyAreas: ["pimlico", "battersea", "stockwell", "waterloo", "westminster"],
    landmarks: ["Vauxhall Bridge", "The Oval", "Spring Gardens"],
    propertyTypes: ["Georgian terraces", "Victorian houses", "new-build towers"]
  },
  "barnes": {
    name: "Barnes", slug: "barnes-secondary-glazing",
    lat: "51.4710", lng: "-0.2412", postcode: "SW13", borough: "Richmond upon Thames", region: "South West London",
    nearbyAreas: ["richmond", "putney", "chiswick", "hammersmith"],
    landmarks: ["Barnes Common", "WWT London Wetland Centre", "Barnes Village"],
    propertyTypes: ["Victorian villas", "Edwardian houses", "riverside cottages"]
  },
  "kew": {
    name: "Kew", slug: "kew-secondary-glazing",
    lat: "51.4787", lng: "-0.2933", postcode: "TW9", borough: "Richmond upon Thames", region: "South West London",
    nearbyAreas: ["richmond", "brentford", "chiswick", "barnes"],
    landmarks: ["Royal Botanic Gardens", "Kew Palace", "Kew Green"],
    propertyTypes: ["Georgian houses", "Victorian villas", "Edwardian properties"]
  },
  "acton": {
    name: "Acton", slug: "acton-secondary-glazing",
    lat: "51.5095", lng: "-0.2757", postcode: "W3", borough: "Ealing", region: "West London",
    nearbyAreas: ["ealing", "chiswick", "shepherds-bush", "north-acton", "park-royal"],
    landmarks: ["Acton Park", "Acton High Street"],
    propertyTypes: ["Victorian terraces", "Edwardian houses", "1930s semis"]
  },
  "hammersmith": {
    name: "Hammersmith", slug: "hammersmith-secondary-glazing",
    lat: "51.4928", lng: "-0.2229", postcode: "W6", borough: "Hammersmith and Fulham", region: "West London",
    nearbyAreas: ["fulham", "chiswick", "shepherds-bush", "barnes", "holland-park"],
    landmarks: ["Hammersmith Bridge", "Lyric Theatre", "Hammersmith Apollo"],
    propertyTypes: ["Victorian terraces", "Edwardian houses", "mansion flats"]
  },
  "bayswater": {
    name: "Bayswater", slug: "bayswater-secondary-glazing",
    lat: "51.5128", lng: "-0.1876", postcode: "W2", borough: "City of Westminster", region: "Central London",
    nearbyAreas: ["paddington", "notting-hill", "marylebone", "kensington"],
    landmarks: ["Queensway", "Kensington Gardens", "Whiteleys"],
    propertyTypes: ["Victorian stucco terraces", "mansion blocks", "converted flats"]
  },
  "clerkenwell": {
    name: "Clerkenwell", slug: "clerkenwell-secondary-glazing",
    lat: "51.5234", lng: "-0.1049", postcode: "EC1R", borough: "Islington", region: "Central London",
    nearbyAreas: ["farringdon", "holborn", "city-of-london", "shoreditch", "bloomsbury"],
    landmarks: ["Exmouth Market", "Smithfield Market", "Sadler's Wells"],
    propertyTypes: ["Georgian terraces", "converted warehouses", "loft apartments"]
  },
  "islington": {
    name: "Islington", slug: "islington-secondary-glazing",
    lat: "51.5362", lng: "-0.1033", postcode: "N1", borough: "Islington", region: "North London",
    nearbyAreas: ["clerkenwell", "farringdon", "kings-cross", "shoreditch", "bloomsbury"],
    landmarks: ["Upper Street", "Islington Green", "Almeida Theatre", "Regent's Canal"],
    propertyTypes: ["Georgian terraces", "Victorian townhouses", "converted school buildings"]
  },
  "london-bridge": {
    name: "London Bridge", slug: "london-bridge-secondary-glazing",
    lat: "51.5055", lng: "-0.0864", postcode: "SE1", borough: "Southwark", region: "South London",
    nearbyAreas: ["southwark", "bermondsey", "borough", "bankside", "city-of-london"],
    landmarks: ["The Shard", "Borough Market", "Tower Bridge", "HMS Belfast"],
    propertyTypes: ["Converted warehouses", "Victorian terraces", "new-build apartments"]
  },
  "earls-court": {
    name: "Earl's Court", slug: "earls-court-secondary-glazing",
    lat: "51.4898", lng: "-0.1917", postcode: "SW5", borough: "Royal Borough of Kensington and Chelsea", region: "West London",
    nearbyAreas: ["kensington", "chelsea", "fulham", "south-kensington", "west-brompton"],
    landmarks: ["Earl's Court Station", "Empress State Building"],
    propertyTypes: ["Victorian mansion blocks", "converted flats", "garden square terraces"]
  },
  "regents-park": {
    name: "Regent's Park", slug: "regents-park-secondary-glazing",
    lat: "51.5313", lng: "-0.1570", postcode: "NW1", borough: "Camden/Westminster", region: "Central London",
    nearbyAreas: ["marylebone", "primrose-hill", "st-johns-wood", "fitzrovia", "euston"],
    landmarks: ["Regent's Park", "London Zoo", "Nash Terraces", "Open Air Theatre"],
    propertyTypes: ["Regency Nash terraces", "Victorian villas", "mansion blocks"]
  },
  "holborn": {
    name: "Holborn", slug: "holborn-secondary-glazing",
    lat: "51.5174", lng: "-0.1200", postcode: "WC1", borough: "Camden", region: "Central London",
    nearbyAreas: ["bloomsbury", "covent-garden", "clerkenwell", "farringdon", "strand"],
    landmarks: ["Lincoln's Inn Fields", "Sir John Soane's Museum", "Chancery Lane"],
    propertyTypes: ["Georgian townhouses", "Victorian mansion blocks", "listed buildings"]
  },
  "euston": {
    name: "Euston", slug: "euston-secondary-glazing",
    lat: "51.5282", lng: "-0.1337", postcode: "NW1", borough: "Camden", region: "Central London",
    nearbyAreas: ["kings-cross", "bloomsbury", "fitzrovia", "regents-park"],
    landmarks: ["Euston Station", "British Library", "Wellcome Collection"],
    propertyTypes: ["Georgian terraces", "Victorian houses", "mansion blocks"]
  },
  "farringdon": {
    name: "Farringdon", slug: "farringdon-secondary-glazing",
    lat: "51.5204", lng: "-0.1058", postcode: "EC1", borough: "Islington/City of London", region: "Central London",
    nearbyAreas: ["clerkenwell", "holborn", "city-of-london", "bloomsbury"],
    landmarks: ["Smithfield Market", "Charterhouse", "Hatton Garden"],
    propertyTypes: ["Georgian townhouses", "converted warehouses", "Victorian commercial buildings"]
  },
  "kings-cross": {
    name: "King's Cross", slug: "kings-cross-secondary-glazing",
    lat: "51.5308", lng: "-0.1238", postcode: "N1C", borough: "Camden", region: "Central London",
    nearbyAreas: ["euston", "bloomsbury", "clerkenwell"],
    landmarks: ["King's Cross Station", "St Pancras International", "Coal Drops Yard", "Granary Square"],
    propertyTypes: ["Georgian terraces", "Victorian houses", "converted industrial buildings"]
  },
  "st-james": {
    name: "St James's", slug: "st-james-secondary-glazing",
    lat: "51.5050", lng: "-0.1368", postcode: "SW1Y", borough: "City of Westminster", region: "Central London",
    nearbyAreas: ["mayfair", "westminster", "soho", "covent-garden"],
    landmarks: ["St James's Palace", "Pall Mall", "Jermyn Street", "The Mall"],
    propertyTypes: ["Georgian townhouses", "listed buildings", "mansion blocks"]
  },
  "aldgate": {
    name: "Aldgate", slug: "aldgate-secondary-glazing",
    lat: "51.5137", lng: "-0.0755", postcode: "E1", borough: "Tower Hamlets", region: "East London",
    nearbyAreas: ["whitechapel", "spitalfields", "city-of-london", "brick-lane"],
    landmarks: ["Aldgate Square", "Whitechapel Gallery", "Tower of London"],
    propertyTypes: ["Victorian terraces", "converted warehouses", "modern apartments"]
  },
  "brick-lane": {
    name: "Brick Lane", slug: "brick-lane-secondary-glazing",
    lat: "51.5215", lng: "-0.0717", postcode: "E1", borough: "Tower Hamlets", region: "East London",
    nearbyAreas: ["shoreditch", "spitalfields", "whitechapel", "aldgate"],
    landmarks: ["Brick Lane Market", "Old Truman Brewery", "Brick Lane Mosque"],
    propertyTypes: ["Georgian weavers' houses", "Victorian terraces", "converted warehouses"]
  },
  "whitechapel": {
    name: "Whitechapel", slug: "whitechapel-secondary-glazing",
    lat: "51.5152", lng: "-0.0677", postcode: "E1", borough: "Tower Hamlets", region: "East London",
    nearbyAreas: ["aldgate", "brick-lane", "spitalfields", "shoreditch"],
    landmarks: ["Whitechapel Gallery", "Royal London Hospital", "Whitechapel Market"],
    propertyTypes: ["Victorian terraces", "Edwardian houses", "converted warehouses"]
  },
  "bankside": {
    name: "Bankside", slug: "bankside-secondary-glazing",
    lat: "51.5085", lng: "-0.0992", postcode: "SE1", borough: "Southwark", region: "South London",
    nearbyAreas: ["southwark", "south-bank", "london-bridge", "waterloo", "borough"],
    landmarks: ["Tate Modern", "Shakespeare's Globe", "Millennium Bridge"],
    propertyTypes: ["Converted warehouses", "Georgian terraces", "new-build riverside apartments"]
  },
  "south-bank": {
    name: "South Bank", slug: "south-bank-secondary-glazing",
    lat: "51.5064", lng: "-0.1163", postcode: "SE1", borough: "Lambeth", region: "South London",
    nearbyAreas: ["waterloo", "bankside", "southwark", "westminster"],
    landmarks: ["National Theatre", "London Eye", "South Bank Centre", "BFI"],
    propertyTypes: ["Converted warehouses", "brutalist estates", "riverside apartments"]
  },
  "elephant-and-castle": {
    name: "Elephant & Castle", slug: "elephant-and-castle-secondary-glazing",
    lat: "51.4943", lng: "-0.1006", postcode: "SE1", borough: "Southwark", region: "South London",
    nearbyAreas: ["southwark", "waterloo", "vauxhall", "bermondsey", "borough"],
    landmarks: ["Elephant & Castle Shopping Centre", "Imperial War Museum", "Ministry of Sound"],
    propertyTypes: ["Victorian terraces", "council estates", "new-build developments"]
  },
  "bermondsey": {
    name: "Bermondsey", slug: "bermondsey-secondary-glazing",
    lat: "51.4979", lng: "-0.0637", postcode: "SE1", borough: "Southwark", region: "South London",
    nearbyAreas: ["london-bridge", "southwark", "borough", "greenwich"],
    landmarks: ["Bermondsey Street", "Fashion and Textile Museum", "Maltby Street Market"],
    propertyTypes: ["Converted warehouses", "Victorian terraces", "new-build apartments"]
  },
  "southwark": {
    name: "Southwark", slug: "southwark-secondary-glazing",
    lat: "51.5036", lng: "-0.0934", postcode: "SE1", borough: "Southwark", region: "South London",
    nearbyAreas: ["london-bridge", "bankside", "bermondsey", "borough", "elephant-and-castle"],
    landmarks: ["Southwark Cathedral", "Borough Market", "The Shard"],
    propertyTypes: ["Victorian terraces", "converted warehouses", "listed buildings"]
  },
  "borough": {
    name: "Borough", slug: "borough-secondary-glazing",
    lat: "51.5019", lng: "-0.0930", postcode: "SE1", borough: "Southwark", region: "South London",
    nearbyAreas: ["london-bridge", "southwark", "elephant-and-castle", "bermondsey", "bankside"],
    landmarks: ["Borough Market", "Guy's Hospital", "Borough High Street"],
    propertyTypes: ["Georgian terraces", "Victorian houses", "converted warehouses"]
  },
  "stockwell": {
    name: "Stockwell", slug: "stockwell-secondary-glazing",
    lat: "51.4720", lng: "-0.1220", postcode: "SW9", borough: "Lambeth", region: "South London",
    nearbyAreas: ["brixton", "clapham", "vauxhall", "balham"],
    landmarks: ["Stockwell War Memorial", "Stockwell Park"],
    propertyTypes: ["Victorian terraces", "Edwardian houses", "council estates"]
  },
  "maida-vale": {
    name: "Maida Vale", slug: "maida-vale-secondary-glazing",
    lat: "51.5275", lng: "-0.1866", postcode: "W9", borough: "City of Westminster", region: "Central London",
    nearbyAreas: ["st-johns-wood", "paddington", "notting-hill", "bayswater"],
    landmarks: ["Little Venice", "Maida Vale Studios", "Regent's Canal"],
    propertyTypes: ["Edwardian mansion blocks", "Victorian villas", "canal-side flats"]
  },
  "swiss-cottage": {
    name: "Swiss Cottage", slug: "swiss-cottage-secondary-glazing",
    lat: "51.5432", lng: "-0.1740", postcode: "NW3", borough: "Camden", region: "North London",
    nearbyAreas: ["hampstead", "st-johns-wood", "primrose-hill"],
    landmarks: ["Swiss Cottage Library", "Hampstead Theatre"],
    propertyTypes: ["Victorian houses", "Edwardian mansion blocks", "1930s flats"]
  },
  "brompton": {
    name: "Brompton", slug: "brompton-secondary-glazing",
    lat: "51.4937", lng: "-0.1693", postcode: "SW3", borough: "Royal Borough of Kensington and Chelsea", region: "West London",
    nearbyAreas: ["south-kensington", "chelsea", "knightsbridge", "earls-court"],
    landmarks: ["Brompton Oratory", "Brompton Cemetery"],
    propertyTypes: ["Victorian terraces", "mansion blocks", "stucco-fronted houses"]
  },
  "west-brompton": {
    name: "West Brompton", slug: "west-brompton-secondary-glazing",
    lat: "51.4870", lng: "-0.1952", postcode: "SW10", borough: "Royal Borough of Kensington and Chelsea", region: "West London",
    nearbyAreas: ["earls-court", "fulham", "chelsea", "brompton"],
    landmarks: ["Brompton Cemetery", "Stamford Bridge"],
    propertyTypes: ["Victorian terraces", "mansion blocks", "converted flats"]
  },
  "shepherds-bush": {
    name: "Shepherd's Bush", slug: "shepherds-bush-secondary-glazing",
    lat: "51.5045", lng: "-0.2263", postcode: "W12", borough: "Hammersmith and Fulham", region: "West London",
    nearbyAreas: ["holland-park", "hammersmith", "acton", "notting-hill"],
    landmarks: ["Westfield London", "Shepherd's Bush Empire", "Shepherd's Bush Green"],
    propertyTypes: ["Victorian terraces", "Edwardian houses", "mansion flats"]
  },
  "twickenham": {
    name: "Twickenham", slug: "twickenham-secondary-glazing",
    lat: "51.4490", lng: "-0.3375", postcode: "TW1", borough: "Richmond upon Thames", region: "South West London",
    nearbyAreas: ["richmond", "kew", "hampton", "isleworth"],
    landmarks: ["Twickenham Stadium", "Marble Hill House", "York House"],
    propertyTypes: ["Georgian houses", "Victorian villas", "Edwardian terraces"]
  },
  "kingston": {
    name: "Kingston", slug: "kingston-secondary-glazing",
    lat: "51.4085", lng: "-0.3064", postcode: "KT1", borough: "Kingston upon Thames", region: "South West London",
    nearbyAreas: ["richmond", "wimbledon", "new-malden", "hampton"],
    landmarks: ["Kingston Market Place", "Hampton Court Palace", "River Thames"],
    propertyTypes: ["Victorian terraces", "Edwardian houses", "Tudor-style properties"]
  },
  "isleworth": {
    name: "Isleworth", slug: "isleworth-secondary-glazing",
    lat: "51.4688", lng: "-0.3283", postcode: "TW7", borough: "Hounslow", region: "West London",
    nearbyAreas: ["brentford", "richmond", "twickenham", "kew"],
    landmarks: ["Syon House", "London Apprentice pub"],
    propertyTypes: ["Victorian terraces", "Edwardian houses", "riverside properties"]
  },
  "brentford": {
    name: "Brentford", slug: "brentford-secondary-glazing",
    lat: "51.4893", lng: "-0.3088", postcode: "TW8", borough: "Hounslow", region: "West London",
    nearbyAreas: ["chiswick", "isleworth", "kew", "ealing"],
    landmarks: ["Brentford FC Stadium", "Boston Manor House", "Grand Union Canal"],
    propertyTypes: ["Victorian terraces", "Edwardian houses", "canal-side conversions"]
  },
  "roehampton": {
    name: "Roehampton", slug: "roehampton-secondary-glazing",
    lat: "51.4508", lng: "-0.2381", postcode: "SW15", borough: "Wandsworth", region: "South West London",
    nearbyAreas: ["putney", "wimbledon", "barnes", "richmond"],
    landmarks: ["Roehampton University", "Richmond Park"],
    propertyTypes: ["1960s estates", "Victorian houses", "Georgian properties"]
  },
  "hampton": {
    name: "Hampton", slug: "hampton-secondary-glazing",
    lat: "51.4178", lng: "-0.3638", postcode: "TW12", borough: "Richmond upon Thames", region: "South West London",
    nearbyAreas: ["twickenham", "kingston", "richmond"],
    landmarks: ["Hampton Court Palace", "Bushy Park"],
    propertyTypes: ["Victorian villas", "Edwardian houses", "period cottages"]
  },
  "new-malden": {
    name: "New Malden", slug: "new-malden-secondary-glazing",
    lat: "51.4041", lng: "-0.2556", postcode: "KT3", borough: "Kingston upon Thames", region: "South West London",
    nearbyAreas: ["kingston", "wimbledon", "merton"],
    landmarks: ["New Malden High Street"],
    propertyTypes: ["1930s semis", "Victorian terraces", "Edwardian houses"]
  },
  "strand": {
    name: "Strand", slug: "strand-secondary-glazing",
    lat: "51.5115", lng: "-0.1197", postcode: "WC2R", borough: "City of Westminster", region: "Central London",
    nearbyAreas: ["covent-garden", "holborn", "waterloo", "south-bank"],
    landmarks: ["Somerset House", "The Savoy", "Adelphi Theatre"],
    propertyTypes: ["Georgian buildings", "Victorian commercial properties", "listed buildings"]
  },
  "canary-wharf": {
    name: "Canary Wharf", slug: "canary-wharf-secondary-glazing",
    lat: "51.5054", lng: "-0.0235", postcode: "E14", borough: "Tower Hamlets", region: "East London",
    nearbyAreas: ["greenwich", "bermondsey"],
    landmarks: ["One Canada Square", "Crossrail Place", "Museum of London Docklands"],
    propertyTypes: ["Modern apartments", "converted warehouses", "new-build towers"]
  },
  "crystal-palace": {
    name: "Crystal Palace", slug: "crystal-palace-secondary-glazing",
    lat: "51.4183", lng: "-0.0769", postcode: "SE19", borough: "Bromley/Croydon", region: "South London",
    nearbyAreas: ["dulwich", "norwood", "streatham"],
    landmarks: ["Crystal Palace Park", "National Sports Centre"],
    propertyTypes: ["Victorian villas", "Edwardian houses", "1930s semis"]
  },
  "norwood": {
    name: "Norwood", slug: "norwood-secondary-glazing",
    lat: "51.4314", lng: "-0.0846", postcode: "SE27", borough: "Lambeth", region: "South London",
    nearbyAreas: ["dulwich", "crystal-palace", "streatham", "brixton"],
    landmarks: ["West Norwood Cemetery", "Norwood Park"],
    propertyTypes: ["Victorian terraces", "Edwardian houses", "1930s semis"]
  },
  "merton": {
    name: "Merton", slug: "merton-secondary-glazing",
    lat: "51.4098", lng: "-0.1947", postcode: "SW19", borough: "Merton", region: "South West London",
    nearbyAreas: ["wimbledon", "tooting", "new-malden"],
    landmarks: ["Merton Abbey Mills", "Morden Hall Park"],
    propertyTypes: ["Victorian terraces", "1930s semis", "Edwardian houses"]
  },
  "park-royal": {
    name: "Park Royal", slug: "park-royal-secondary-glazing",
    lat: "51.5282", lng: "-0.2751", postcode: "NW10", borough: "Ealing/Brent", region: "West London",
    nearbyAreas: ["acton", "ealing", "north-acton"],
    landmarks: ["Park Royal industrial estate"],
    propertyTypes: ["Victorian terraces", "1930s houses", "industrial conversions"]
  },
  "north-acton": {
    name: "North Acton", slug: "north-acton-secondary-glazing",
    lat: "51.5235", lng: "-0.2599", postcode: "NW10", borough: "Ealing", region: "West London",
    nearbyAreas: ["acton", "park-royal", "ealing"],
    landmarks: ["North Acton Station", "Victoria Road Park"],
    propertyTypes: ["Victorian terraces", "new-build apartments", "1930s houses"]
  },
  "greenford": {
    name: "Greenford", slug: "greenford-secondary-glazing",
    lat: "51.5294", lng: "-0.3462", postcode: "UB6", borough: "Ealing", region: "West London",
    nearbyAreas: ["ealing", "north-acton", "park-royal"],
    landmarks: ["Horsenden Hill", "Greenford Quay"],
    propertyTypes: ["1930s semis", "Victorian terraces", "modern apartments"]
  },
  "victoria": {
    name: "Victoria", slug: "victoria-secondary-glazing",
    lat: "51.4964", lng: "-0.1439", postcode: "SW1", borough: "City of Westminster", region: "Central London",
    nearbyAreas: ["westminster", "pimlico", "belgravia", "chelsea", "knightsbridge"],
    landmarks: ["Victoria Station", "Buckingham Palace", "Westminster Cathedral", "Victoria Street"],
    propertyTypes: ["Victorian terraces", "mansion blocks", "Georgian townhouses", "converted offices"]
  },
  "kentish-town": {
    name: "Kentish Town", slug: "kentish-town-secondary-glazing",
    lat: "51.5504", lng: "-0.1403", postcode: "NW5", borough: "Camden", region: "North London",
    nearbyAreas: ["hampstead", "highgate", "primrose-hill", "swiss-cottage"],
    landmarks: ["Kentish Town Road", "The Forum", "Hampstead Heath Extension", "Bartholomew Estate"],
    propertyTypes: ["Victorian terraces", "converted flats", "Edwardian cottages"]
  },
  "kilburn": {
    name: "Kilburn", slug: "kilburn-secondary-glazing",
    lat: "51.5367", lng: "-0.2042", postcode: "NW6", borough: "Camden/Brent", region: "North London",
    nearbyAreas: ["maidavale", "hampstead", "wembley", "queens-park"],
    landmarks: ["Kilburn High Road", "The Kiln Theatre", "Mapesbury Dell", "Brondesbury Park"],
    propertyTypes: ["Victorian terraces", "mansion blocks", "converted maisonettes"]
  },
  "surbiton": {
    name: "Surbiton", slug: "surbiton-secondary-glazing",
    lat: "51.3934", lng: "-0.3074", postcode: "KT6", borough: "Kingston upon Thames", region: "South West London",
    nearbyAreas: ["kingston", "wimbledon", "new-malden", "hampton"],
    landmarks: ["Surbiton Station", "Clarence Street", "Berrylands Nature Reserve", "Surbiton Clock Tower"],
    propertyTypes: ["Victorian terraces", "Edwardian houses", "1930s semis", "riverside apartments"]
  }
};

/**
 * Get area data by slug (without the -secondary-glazing suffix)
 */
export const getAreaByKey = (key: string): AreaData | undefined => {
  return londonAreaCoordinates[key];
};

/**
 * Get area data by full slug (with -secondary-glazing suffix)
 */
export const getAreaBySlug = (slug: string): AreaData | undefined => {
  const key = slug.replace('-secondary-glazing', '');
  return londonAreaCoordinates[key];
};

/**
 * Get all areas in a specific region
 */
export const getAreasByRegion = (region: string): AreaData[] => {
  return Object.values(londonAreaCoordinates).filter(a => a.region === region);
};

/**
 * Get all unique regions
 */
export const getAllRegions = (): string[] => {
  return [...new Set(Object.values(londonAreaCoordinates).map(a => a.region))];
};
